import { AlertTitle, Card, CardContent, Stack } from "@altimateai/lego";
import { Button, Alert, AlertDescription } from "@altimateai/lego";
import styles from "./styles.module.scss";
import classnames from "classnames";
import { ColumnDatatype } from "../../components/column";
import { useMemo, useState } from "react";
import { ColorInsightTags, ColorTag } from "../../components/tags";
import { DebouncedInput } from "../../components/form";
import { scroll_style } from "../../components/scrollContainer";
import { default as DBTIcon } from "../../assets/icons/dbt.svg?react";
import { useAppState } from "@/modules/app/useAppContext";
import dayjs from "dayjs";
import { IconExclamationCircle } from "@tabler/icons-react";
import { DbtColumn, DbtModel } from "@/types/dbtModels";

const ColumnCard = ({ column }: { column: DbtColumn }) => {
  return (
    <div className={styles.column_card}>
      <Stack align="center">
        <ColumnDatatype datatype={column.datatype} />
        <div className="ms-2">{column.name}</div>
        <div className="spacer" />
      </Stack>
      <ColorTag label={column.datatype} color={styles.column_tag} />
      {column.description && (
        <>
          <div className="mb-1 divider" />
          <Stack gap="xs" align="center">
            <div>Description</div>
          </Stack>
          <div className="text-muted font-normal">{column.description}</div>
        </>
      )}
      <ColorInsightTags tags={(column.tags || []) as string[]} />
    </div>
  );
};

const ColumnSection = ({ columns }: { columns: DbtColumn[] }) => {
  const [search, setSearch] = useState("");
  const filteredColumns = useMemo(() => {
    if (!search) return columns;
    const _search = search.toLowerCase();
    return columns.filter((c: DbtColumn) => c.name.includes(_search));
  }, [columns, search]);

  return (
    <Card className={classnames("mb-0", styles.bg_gray)}>
      <CardContent className="al-pt-4 al-h-full">
        <Stack direction="column" gap="sm" className="al-h-full">
          <div className="fs-5 fw-semibold mb-2">Columns</div>
          <DebouncedInput
            placeholder="Search column"
            onChange={setSearch}
            aria-label="Search columns"
          />
          {search ? (
            <div className="text-primary">{filteredColumns.length} results found</div>
          ) : (
            <div>{columns.length} columns</div>
          )}
          <Stack direction="column" gap="sm">
            {filteredColumns.map(c => (
              <ColumnCard key={c.rk} column={c} />
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const HeaderSection = ({
  table,
  onDetailsClick,
}: {
  table: DbtModel;
  onDetailsClick: (table: DbtModel) => void;
}) => {
  return (
    <Stack align="center">
      <div className="me-2 icon-md">
        <DBTIcon className="al-h-6 al-w-6" />
      </div>
      <div className={styles.column_table_name}>{table.model_name}</div>
      <div className="spacer" />
      <Button
        className="mw-8"
        size="sm"
        onClick={e => {
          e.stopPropagation();
          onDetailsClick(table);
        }}
      >
        View Details
      </Button>
    </Stack>
  );
};

const ColumnsModal = ({
  table,
  onDetailsClick,
}: {
  table: DbtModel | null;
  onDetailsClick: (table: DbtModel) => void;
}) => {
  const { staleResourcesEnabled } = useAppState();

  // Early return if table is null/undefined (can happen during Sheet close animation)
  if (!table) return null;

  return (
    <Stack direction="column" gap="md" className={classnames("al-h-full p-3 m-1", scroll_style)}>
      <HeaderSection table={table} onDetailsClick={onDetailsClick} />
      {staleResourcesEnabled && table.is_stale && table.last_seen && (
        <Alert variant={"warning"}>
          <IconExclamationCircle className={"al-size-5"} />
          <AlertTitle className={"al-text-base"}>This resource may be outdated</AlertTitle>
          <AlertDescription>
            This resource was last seen on {dayjs(table.last_seen).format("DD MMM YYYY, hh:mm A")}
          </AlertDescription>
        </Alert>
      )}
      {table.description && <div className="text-muted font-normal">{table.description}</div>}
      <ColumnSection columns={table.columns} />
    </Stack>
  );
};

export { ColumnsModal };
